import { Box, Button, css, TextField } from '@mui/material'
import validator from 'email-validator'
import { useState } from 'react'

import { useMQ } from '../hooks/useMQ'
import { subService } from '../services/subscription'
import { AlreadySubscribedError } from '../utils/error'
import { HumaScrollAnimation } from './HumaScrollAnimation'
import HumaLogoIron from './images/huma-logo-iron.png'

export function Subscription() {
  const { isXsSize, isSmSize } = useMQ()
  const [email, setEmail] = useState('')
  const [isValidEmail, setIsValidEmail] = useState(false)
  const [subscribeAlert, setSubscribeAlert] = useState<{
    severity: 'success' | 'warning' | 'error'
    message: string
  }>()
  const [loading, setLoading] = useState(false)

  const getInfoWrapperStyle = () => {
    if (isSmSize) {
      return css`
        margin-bottom: 20px;
        width: 100%;
        text-align: center;
        padding-right: 0 !important;
      `
    }
  }

  const getRightStyle = () => {
    if (isXsSize) {
      return css`
        flex-direction: column;
        width: calc(100% - 64px);
      `
    }
    if (isSmSize) {
      return css`
        width: 100%;
      `
    }
  }

  const getInfoText1Style = () => {
    if (isXsSize) {
      return css`
        font-size: 24px;
        line-height: 123.5%;
        letter-spacing: 0.25px;
      `
    }
  }

  const getInfoText2Style = () => {
    if (isXsSize) {
      return css`
        font-size: 16px;
        line-height: 22px;
      `
    }
  }

  const getBtnStyle = () => {
    if (isXsSize) {
      return css`
        width: 100%;
        margin-top: ${subscribeAlert ? '30px' : '8px'};
      `
    }
  }

  const styles = {
    wrapper: css`
      margin-top: ${isSmSize ? '160px' : '240px'};
      margin-bottom: 64px;
      z-index: 2;
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    `,
    logoWrapper: css`
      width: ${isXsSize ? '72px' : '120px'};
      height: ${isXsSize ? '73px' : '122px'};
      flex-shrink: 0;
      aspect-ratio: 60/61;
      border-radius: 24px;
      background: linear-gradient(180deg, #2e2d32 5.53%, #151517 100%);
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        max-width: ${isXsSize ? '47px' : '78px'};
        max-height: ${isXsSize ? '48px' : '80px'};
      }
    `,
    infoWrapper: css`
      margin-top: 36px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      ${getInfoWrapperStyle()}
    `,
    infoText1: css`
      font-family: 'Red-Hat-Display';
      font-weight: 700;
      font-size: 48px;
      line-height: 116.7%;
      letter-spacing: 0.25px;

      background: radial-gradient(
        70% 40% at 50% 0%,
        rgba(255, 255, 255, 0.6) 0%,
        #fff 100%
      );
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      ${getInfoText1Style()}
    `,
    infoText2: css`
      text-align: center;
      font-size: 18px;
      color: #686868;
      margin-top: 4px;
      font-weight: 400;
      line-height: 150%;
      letter-spacing: 0.15px;
      ${getInfoText2Style()}
    `,
    actionWrapper: css`
      margin-top: ${isXsSize ? '24px' : '64px'};
      display: flex;
      position: relative;
      ${getRightStyle()}
    `,
    infoEmailWrapper: css`
      padding: 0 16px 0 0 !important;
      width: 100%;
    `,
    infoEmailInput: css`
      border-radius: 32px;

      input {
        width: ${isXsSize ? '100%' : '380px'};
        color: #b8b8b8;
        border-radius: 32px;
        height: 48px;
        box-sizing: border-box;
        border-radius: 32px;
        background: #313131;

        &::placeholder {
          color: #b8b8b8;
        }
      }
      .MuiFormHelperText-root {
        color: #b8b8b8 !important;
        font-size: 14px;
        position: absolute;
        top: 45px;
        width: 300px;
        margin: 4px 0 0 0;
      }
      .MuiOutlinedInput-notchedOutline {
        border-radius: 32px;
      }
    `,
    btnWrapper: css`
      padding: 0 !important;
      cursor: ${isValidEmail ? 'unset' : 'not-allowed'};
      & .Mui-disabled {
        background: #000000 !important;
        color: #fbfbfc !important;
      }
    `,
    btn: css`
      width: 113px;
      height: 48px;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.08);
      font-size: 14px;
      text-transform: none;
      ${getBtnStyle()}
    `,
    backgroundImg: css`
      position: absolute;
    `,
  }

  const checkEmail = (email: string) => {
    const isValidEmail = email === '' || validator.validate(email)
    setIsValidEmail(isValidEmail)
    if (!isValidEmail) {
      setSubscribeAlert({
        severity: 'warning',
        message: 'Please enter valid email address',
      })
    } else {
      setSubscribeAlert(undefined)
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value
    setEmail(newEmail)
    checkEmail(newEmail)
  }

  const handleEmailBlur = () => {
    checkEmail(email)
  }

  const handleSubscribe = async () => {
    if (!isValidEmail) {
      return
    }
    try {
      setLoading(true)
      await subService.subscribe(email)
      setSubscribeAlert({
        severity: 'success',
        message: 'You have successfully subscribed',
      })
    } catch (e) {
      if (e instanceof AlreadySubscribedError) {
        setSubscribeAlert({
          severity: 'warning',
          message: 'You are already subscribed',
        })
      } else {
        setSubscribeAlert({
          severity: 'error',
          message: 'Something went wrong',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAlertClose = () => {
    // @ts-ignore
    setSubscribeAlert(undefined)
  }

  return (
    <HumaScrollAnimation>
      <Box css={styles.wrapper}>
        <Box css={styles.logoWrapper}>
          <img src={HumaLogoIron} alt='huma-logo-iron'></img>
        </Box>
        <Box css={styles.infoWrapper}>
          <Box css={styles.infoText1}>Stay in the know</Box>
          <Box css={styles.infoText2}>
            Stay updated with the latest from Huma.
          </Box>
        </Box>
        <Box css={styles.actionWrapper}>
          <Box css={styles.infoEmailWrapper}>
            <TextField
              css={styles.infoEmailInput}
              fullWidth
              variant='outlined'
              placeholder='Enter your email'
              size='small'
              value={email}
              onChange={handleEmailChange}
              onFocus={handleAlertClose}
              onBlur={handleEmailBlur}
              error={!!subscribeAlert}
              helperText={subscribeAlert?.message}
              inputProps={{
                style: { fontFamily: 'Red-Hat-Display', fontSize: '14px' },
              }}
              InputLabelProps={{
                style: { fontFamily: 'Red-Hat-Display' },
              }}
            />
          </Box>
          <Box css={styles.btnWrapper}>
            <Button
              css={styles.btn}
              variant='contained'
              onClick={handleSubscribe}
            >
              {loading ? 'Loading...' : 'Subscribe'}
            </Button>
          </Box>
        </Box>
      </Box>
    </HumaScrollAnimation>
  )
}
